/* eslint-disable camelcase */
import { ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { screen, colors, fonts } from 'components/styles/variables';
import LoadingSpinner from 'components/Shared/LoadingSpinner';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';

export interface ContactCtaProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  contacted: boolean;
  className?: string;
  renderLoadingSpinner: boolean;
  quickContactEnabled: boolean;
}

export default function ContactCta({
  contacted,
  onClick,
  className,
  renderLoadingSpinner,
  quickContactEnabled,
  ...props
}: ContactCtaProps): JSX.Element {
  const { t } = useTranslation();
  const { getDecision } = useOptimizelyDecisions();
  const testLeadCTA = getDecision('testLeadCTA');
  const translationVariant = testLeadCTA
    ? 'srp.contactCta.requestInfo'
    : 'srp.contactCta.checkAvailability';

  const buttonText = contacted ? t('srp.contactCta.contacted') : t(translationVariant);

  function getButtonText(): string | JSX.Element {
    if (renderLoadingSpinner) {
      return <LoadingSpinner containerSize={22} spinnerSize={22} isWhite />;
    }
    return buttonText;
  }

  return (
    <>
      <button
        className={classNames(
          'contact-cta',
          `${contacted ? 'contacted' : 'not-contacted'}`,
          className
        )}
        onClick={onClick}
        aria-label={buttonText}
        disabled={contacted}
        {...props}
      >
        {getButtonText()}
      </button>
      <style jsx>{`
        .contact-cta {
          width: 140px;
          height: 36px;
          text-align: center;
          border-radius: 8px;
          font-family: ${fonts.default};
          font-weight: 600;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.2px;
          padding: 0 16px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: ${quickContactEnabled ? 'all 0.4s ease, color 1s ease-in' : 'none'};
        }

        .not-contacted {
          cursor: pointer;
          color: ${colors.WHITE};
          border: none;
          background-color: ${colors.ALR1};
          background-size: 200% 100%;
          background-position: right;
        }

        .contacted {
          color: ${colors.G3};
          border: 1px solid ${colors.G2};
          background-color: ${colors.WHITE};
        }

        @media (hover: hover) and (pointer: fine) {
          .not-contacted:hover {
            background-color: ${colors.ALR2};
            color: ${colors.WHITE};
          }
        }

        .hidden {
          visibility: hidden;
        }

        .contact-cta-mobile {
          visibility: visible;
        }

        @media (min-width: ${screen.smMin}) {
          .contact-cta-mobile {
            visibility: hidden;
            display: none;
          }
        }

        .contact-cta-desktop {
          display: none;
        }

        @media (min-width: ${screen.smMin}) {
          .contact-cta-desktop {
            display: flex;
          }
        }
      `}</style>
    </>
  );
}
